import { useEffect, useMemo, useRef, useState } from "react";
import "@/App.css";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import Sidebar from "@/components/Sidebar";
import DocumentForm from "@/components/DocumentForm";
import ProfileDialog from "@/components/ProfileDialog";
import PreviewDialog from "@/components/PreviewDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROFILE_KEY = "papirko_profile";
const todayHR = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
};

export default function App() {
  const [catalog, setCatalog] = useState({ categories: [], documents: [] });
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState({});
  const [profile, setProfile] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(PROFILE_KEY)) || {};
    } catch {
      return {};
    }
  });
  const [profileOpen, setProfileOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/documents`)
      .then((res) => setCatalog(res.data))
      .catch(() => toast.error("Greška pri učitavanju popisa dokumenata."));
  }, []);

  const contentRef = useRef(null);
  const lastHeightRef = useRef(0);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const send = () => {
      const h = Math.ceil(el.getBoundingClientRect().height);
      if (h > 0 && Math.abs(h - lastHeightRef.current) > 1) {
        lastHeightRef.current = h;
        window.parent.postMessage({ type: "papirko-height", height: h }, "*");
      }
    };
    send();
    const ro = new ResizeObserver(send);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const selectedDoc = useMemo(
    () => catalog.documents.find((d) => d.id === selectedId) || null,
    [catalog, selectedId]
  );

  const initFormData = (doc, prof) => {
    const data = {};
    doc.fields.forEach((f) => {
      if (f.profile && prof[f.profile]) data[f.name] = prof[f.profile];
      if (f.default && !data[f.name]) data[f.name] = f.default;
      if (f.name === "datum") data.datum = todayHR();
    });
    return data;
  };

  const handleSelect = (doc) => {
    setSelectedId(doc.id);
    setFormData(initFormData(doc, profile));
    setTimeout(() => {
      document.getElementById("form-area")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  const handleAutofill = () => {
    if (!selectedDoc) return;
    if (!Object.keys(profile).length) {
      toast.info("Najprije ispunite 'Moj profil' u gornjem desnom kutu.");
      setProfileOpen(true);
      return;
    }
    const next = { ...formData };
    selectedDoc.fields.forEach((f) => {
      if (f.profile && profile[f.profile]) next[f.name] = profile[f.profile];
    });
    setFormData(next);
    toast.success("Podaci iz profila su popunjeni.");
  };

  const saveProfile = (p) => {
    setProfile(p);
    localStorage.setItem(PROFILE_KEY, JSON.stringify(p));
    setProfileOpen(false);
    toast.success("Profil je spremljen u vaš preglednik.");
  };

  const validate = () => {
    const missing = selectedDoc.fields
      .filter((f) => f.required && !String(formData[f.name] || "").trim())
      .map((f) => f.label);
    if (missing.length) {
      toast.error(`Ispunite obavezna polja: ${missing.slice(0, 4).join(", ")}${missing.length > 4 ? "…" : ""}`);
      return false;
    }
    return true;
  };

  const buildPayload = () => {
    const data = { ...formData };
    if (profile.logo) data._logo = profile.logo;
    return data;
  };

  const handleGenerate = async (format) => {
    if (!selectedDoc || !validate()) return;
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/generate`,
        { doc_id: selectedDoc.id, data: buildPayload(), format },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      a.download = match ? match[1] : `dokument.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Dokument je generiran (${format === "pdf" ? "PDF" : ".docx"}).`);
    } catch {
      toast.error("Greška pri generiranju dokumenta.");
    } finally {
      setBusy(false);
    }
  };

  const handlePreview = async () => {
    if (!selectedDoc || !validate()) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/preview`, { doc_id: selectedDoc.id, data: buildPayload() });
      setPreviewContent(res.data.content);
    } catch {
      toast.error("Greška pri izradi pregleda.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/60">
      <Toaster position="top-center" richColors />
      <div ref={contentRef}>
        <Header onProfileClick={() => setProfileOpen(true)} />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Hero docCount={catalog.documents.length} />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
          <Sidebar
            categories={catalog.categories}
            documents={catalog.documents}
            selectedId={selectedId}
            onSelect={handleSelect}
          />
          <div id="form-area" className="lg:col-span-8 scroll-mt-4">
            <DocumentForm
              doc={selectedDoc}
              formData={formData}
              setFormData={setFormData}
              onAutofill={handleAutofill}
              onGenerate={handleGenerate}
              onPreview={handlePreview}
              busy={busy}
            />
          </div>
        </div>
        <footer className="mt-10 pb-6 text-center text-xs text-slate-400" data-testid="app-footer">
          Papirko · Predlošci su informativne prirode · Vaši podaci ostaju u vašem pregledniku
          i nisu vidljivi nikome drugome
        </footer>
      </main>
      </div>
      <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} profile={profile} onSave={saveProfile} />
      <PreviewDialog
        content={previewContent}
        onClose={() => setPreviewContent(null)}
        onGenerate={handleGenerate}
        busy={busy}
      />
    </div>
  );
}
